import React, { useEffect, useState } from 'react';
import { Form, Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Grid, Typography, Card, CircularProgress, MenuItem, Breadcrumbs, Divider } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate, useParams, Link } from 'react-router-dom';
 
import InputField from 'components/formFields/InputField';
import TextAreaField from 'components/formFields/TextAreaField';
import AutocompleteField from 'components/formFields/autocompleteField';
// import SelectField from 'components/formFields/SelectField';
import { useResponseHandler } from 'hooks/useResponseHandler';
import { getApplicationDataDropDownStore } from 'store/app-releases';
import { getPermissionDetailStore, updatePermissionStore } from 'store/permissions/thunks';

import formModel from './components/formik/edit/permissionsFormModel';
import formInitialValues from './components/formik/edit/formInitialValues';
import validationSchema from './components/formik/edit/validationSchema';
import { MapFormToPermissions, MapPermissionsToForm } from './mapper/permissions.mapper';
import { Save, InfoOutlined, Edit } from '@mui/icons-material';
import { InfoModal } from 'components/generics/InfoModal';
import { IconButton } from '@mui/material';

const StyledCard = styled(Card)(({ theme }) => ({
  marginTop: 50,
  padding: 24,
  borderRadius: 16,
  boxShadow: '0px 12px 24px -4px rgba(145, 158, 171, 0.12), 0px 0px 2px 0px rgba(145, 158, 171, 0.2)',
  border: '1px solid rgba(145, 158, 171, 0.16)'
}));

export const EditPermissions = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();
  const { handleResponse, ResponseComponents } = useResponseHandler();
  const [openInfoModal, setOpenInfoModal] = useState(false);
  
  const [initialData, setInitialData] = useState<any>(null);
  
  const { applications_for_select } = useSelector((state: any) => state.appReleases);
  const { permission_detail } = useSelector((state: any) => state.permissions);

  useEffect(() => {
    dispatch(getApplicationDataDropDownStore());
    if (id) {
      dispatch(getPermissionDetailStore(id));
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (permission_detail && permission_detail.id === id) {
      setInitialData(MapPermissionsToForm(permission_detail));
    }
  }, [permission_detail, id]);

  const _handleSubmit = async (values: any, actions: any) => {
    try {
      const payload = MapFormToPermissions(values);
      const res = await dispatch(updatePermissionStore(id as string, payload));
      
      await handleResponse(res, 'Permiso actualizado', true);
      
      if (!res?.ok) {
        actions.setSubmitting(false);
        return false;
      }
      
      
    } catch (error) {
      actions.setSubmitting(false);
    }
  };

  const { formField } = formModel;

 

  return (
    <Box sx={{ width: '100%' }}>
      {ResponseComponents}

      <Typography fontSize={25} color="textPrimary" sx={{ fontWeight: 700 }}>
        Editar Permiso
      </Typography>

      <Breadcrumbs aria-label="breadcrumb" sx={{ padding: 2 }}>
          <Typography color="textPrimary" sx={{ fontWeight: 700 }}>Manager</Typography>
          <Link  to="/manager/permisos">
            Listar Permisos
          </Link>
          <Typography color="textPrimary">Editar Permiso</Typography>
      </Breadcrumbs>
      
      <Divider sx={{ marginBottom: 3 }}/>

      <Formik
        initialValues={initialData || formInitialValues}
        validationSchema={validationSchema}
        onSubmit={_handleSubmit}
        enableReinitialize
      >
        {({ isSubmitting, values, setFieldValue }) => {
          
          const currentApp = applications_for_select?.find((app: any) => app.id === values.application_id);
          const childrensLineCount = values.childrens ? String(values.childrens).split('\n').length : 3;
          const dynamicRows = Math.min(Math.max(childrensLineCount + 2, 10), 45);
          
          return (
          <Form id={formModel.formId}>
            <StyledCard>
              <Grid container spacing={3}>
                <Grid  size={{xs:12, md:6}}>
                  <InputField 
                    name={formField.name.name} 
                    label={formField.name.label} 
                    fullWidth 
                  />
                </Grid>
                <Grid  size={{xs:12, md:6}}>
                  <InputField 
                    name={formField.icon.name} 
                    label={formField.icon.label} 
                    autoComplete="off"
                    fullWidth 
                  />
                </Grid>
                <Grid  size={{xs:12, md:6}}>
                  <InputField 
                    name={formField.slug.name} 
                    label={formField.slug.label} 
                    autoComplete="off"
                    fullWidth 
                  />
                </Grid>
                <Grid size={{xs:12, md:6}}>
                  <AutocompleteField
                    name={formField.application_id.name}
                    label={formField.application_id.label}
                    options={applications_for_select}
                  />
                </Grid>
                

                <Grid  size={{xs:12}}>
                  <TextAreaField 
                    name={formField.description.name} 
                    label={formField.description.label} 
                    fullWidth 
                    autoComplete="off"
                    rows={5}
                  />
                </Grid>
                <Grid  size={{xs:12}}>
                  <Box display="flex" alignItems="center" mb={1}>
                    <Typography variant="subtitle2" sx={{ color: '#4b5563', fontWeight: 700 }}>
                      {formField.childrens.label}
                    </Typography>
                    <IconButton color="info" onClick={() => setOpenInfoModal(true)} size="small" sx={{ ml: 1 }}>
                      <InfoOutlined fontSize="small" />
                    </IconButton>
                  </Box>
                  <TextAreaField 
                    name={formField.childrens.name} 
                    label=""
                    fullWidth 
                    autoComplete="off"
                    rows={dynamicRows}
                    sx={{ 
                      fontFamily: 'monospace',
                      '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.9rem' } 
                    }}
                  />
                </Grid>

                <Grid  size={{xs:12}}>
                  <Box display="flex" justifyContent="flex-end" gap={2} mt={2}>
                    <Button
                      variant="outlined"
                      color="inherit"
                      onClick={() => navigate('/manager/permisos')}
                    >
                      Cancelar
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={isSubmitting}
                      startIcon={<Edit />}
                    >
                      Actualizar Permiso
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </StyledCard>
          </Form>
        )}}
      </Formik>

      <InfoModal
          open={openInfoModal}
          setOpen={setOpenInfoModal}
          title="Estructura de Childrens (JSON)"
          description="Este campo define la estructura del menú o submenú para este permiso mediante un arreglo JSON."
          infoText={
              <Box>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                      El formato debe ser un JSON válido que contenga un arreglo de objetos. Cada objeto representa un ítem del menú. Ejemplo:
                  </Typography>
                  <Box component="pre" sx={{ backgroundColor: '#f4f6f8', p: 2, borderRadius: 1, fontSize: '0.8rem', overflowX: 'auto' }}>
{`[
  {
    "to": "/ordenes-servicio",
    "icon": "ManageAccounts",
    "slug": "modulo.ordenes.servicios.usuario",
    "title": "Gestion De Usuarios"
  }
]`}
                  </Box>
              </Box>
          }
          icon="Info"
      />
    </Box>
  );
};
